/**
 * Hooks for /api/ecm-insights/* — public-schema (live Trust Marketplace) data.
 * Differs from useEngagementAnalytics (legacy_trust) in that these endpoints
 * query the live app schema; data volume is currently small but grows with
 * SDOH assessments + care plans + program enrollments adoption.
 */

import { useQuery } from '@tanstack/react-query';
import { authFetch } from '@/lib/authFetch';

const STALE_MS = 10 * 60 * 1000;

async function fetchJson<T>(path: string): Promise<T> {
  const resp = await authFetch(path);
  if (!resp.ok) throw new Error(`ecm-insights ${path} ${resp.status}`);
  return resp.json();
}

export interface ProgramEnrollmentRow {
  program_id: number | null;
  program_name: string | null;
  program_type: string | null;
  total_enrollments: number;
  active: number;
  completed: number;
  pending: number;
  unique_members: number;
  cbos_enrolling: number;
}

export function useProgramEnrollmentFunnel() {
  return useQuery<{ cbo_id: number; scope: string; programs: ProgramEnrollmentRow[] }>({
    queryKey: ['ecm-insights', 'program-enrollment-funnel'],
    queryFn: () => fetchJson('/api/ecm-insights/program-enrollment-funnel'),
    staleTime: STALE_MS,
  });
}

export interface SDOHRiskRow {
  risk_level: string;
  n: number;
}

export function useSDOHCoverage() {
  return useQuery<{
    cbo_id: number;
    scope: string;
    total_members: number;
    assessed_members: number;
    coverage_pct: number | null;
    risk_distribution: SDOHRiskRow[];
  }>({
    queryKey: ['ecm-insights', 'sdoh-coverage'],
    queryFn: () => fetchJson('/api/ecm-insights/sdoh-coverage'),
    staleTime: STALE_MS,
  });
}

export function useCarePlanCoverage() {
  return useQuery<{
    cbo_id: number;
    scope: string;
    total_members: number;
    members_with_care_plan: number;
    coverage_pct: number | null;
  }>({
    queryKey: ['ecm-insights', 'care-plan-coverage'],
    queryFn: () => fetchJson('/api/ecm-insights/care-plan-coverage'),
    staleTime: STALE_MS,
  });
}

export function useAtRiskWithoutPlan() {
  return useQuery<{
    cbo_id: number;
    scope: string;
    metrics: {
      members_at_risk: number;
      without_care_plan: number;
      without_program_enrollment: number;
    } | null;
  }>({
    queryKey: ['ecm-insights', 'at-risk-no-plan'],
    queryFn: () => fetchJson('/api/ecm-insights/at-risk-no-plan'),
    staleTime: STALE_MS,
  });
}
